import ExportedImage from "next-image-export-optimizer";
import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { inject, observer } from "mobx-react";
import { Store } from "stores/store";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";
import Card from "@/components/shared/futureChat/cards/Card";
import ButtonChat from "@/components/shared/futureChat/buttons/ButtonChat";
import { cn } from "@/lib/utils";
import { ImageProcessingContext } from "@/components/shared/imageProcessing/ImageProcessing";
import CardError from "@/components/web/cards/CardError";
import { TrackingContext } from "@/components/shared/tracking/Tracking";

type MaskProps = {
    store?: Store;
    onCall: () => void;
    onGoBack: () => void;
    className?: string;
    selfieOrCall: "call" | "selfie";
};

const Mask: React.FC<MaskProps> = ({ store, onCall, onGoBack, className, selfieOrCall }) => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const {
        updateStartCountdown,
        startCountdown,
        globalLocalized,
        maskLocalized,
        faceNotDetected,
        imageProcessingState,
        chatbotLocalized,
        globalEventLocalized,
    } = store;

    const [count, setCount] = useState(maskLocalized?.countdown || 4);
    const [countdown, setCountdown] = useState(false);

    const { trackView, screenView } = useContext(TrackingContext);
    useEffect(() => {
        screenView('1.3 - Face Recognition Start')
    }, [])

    const { requestToken } = useContext(ImageProcessingContext);

    useEffect(() => {
        if (startCountdown) {
            const timer =
                count > 0
                    ? setInterval(() => {
                        setCount(count - 1);
                    }, 1500)
                    : null;

            if (timer === null) {
                onCall();
                updateStartCountdown(false);
                trackView("maskcountdowncomplete");
            }

            return () => {
                if (timer) clearInterval(timer);
            };
        }
    }, [count, startCountdown]);

    const variants = {
        initial: { scale: 0 },
        animate: { scale: 1.5, transition: { type: "spring", stiffness: 100, duration: 0.5 } },
        exit: { scale: 0, transition: { duration: 0.5 } },
    };
    const callButtonIcon = globalEventLocalized?.maskButtonCall.icon;
    const selfieButtonIcon = globalEventLocalized?.maskButtonSelfie.icon;
    const backButtonIcon = maskLocalized?.buttonTwo.icon;

    return (
        <div className="scrollbar-hidden flex h-full w-full flex-col items-center">
            {globalLocalized?.logo !== undefined && globalLocalized?.logo !== null && (
                <div className="absolute top-[3px] z-[100] h-[80px] w-[125px]">
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(globalLocalized.logo)}
                        alt={getAltTextFromGeneralImage(globalLocalized.logo)}
                        layout="fill"
                        objectFit="cover"
                        objectPosition="center"
                    />
                </div>
            )}
            {startCountdown && (
                <div
                    className={cn(
                        /*
                        The face must be in the center of the camera, but also displayed within the center of the mask.
                        The center of the mask is at y=212 @ h=568, i.e., at 37.32394366% of the viewport height.
                        */
                        "absolute top-[49%] z-[50000] flex w-full -translate-y-1/3 items-center justify-center",
                    )}
                >
                    <motion.div
                        key={count}
                        variants={variants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className="font-frutigerBold text-[36px] text-white"
                    >
                        {count > 0 ? count : ""}
                    </motion.div>
                </div>
            )}
            {!startCountdown && (
                <div
                    className={cn(
                        "absolute bottom-[46px] flex w-full flex-col gap-[8px] px-[16px]",
                        "2xs:bottom-[68px] 2xs:w-[364px] 2xs:px-0",
                        "z-[100]",
                    )}
                >
                    <motion.div
                        initial={{ opacity: 1 }}
                        animate={{ opacity: startCountdown ? 0 : 1, scale: startCountdown ? 0 : 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        {!imageProcessingState.noFaceFound && !faceNotDetected ? (
                            <Card className="gap-[5px] px-[28px] py-[15px]">
                                {maskLocalized?.hintBulletPoints.map((hint, index) => (
                                    <div key={index} className="flex items-center gap-[10px]">
                                        {hint?.icon !== undefined && hint?.icon !== null && (
                                            <div className="relative h-[33px] max-h-[33px] min-h-[33px] w-[33px] min-w-[33px] max-w-[33px]">
                                                <ExportedImage
                                                    src={getImageUrlFromGeneralImage(hint?.icon)}
                                                    alt={getAltTextFromGeneralImage(hint?.icon)}
                                                    layout="fill"
                                                    objectFit="contain"
                                                />
                                            </div>
                                        )}
                                        <div className="font-frutigerBold text-[12px] font-bold leading-[135%] tracking-minus-2 text-white">
                                            {hint?.text}
                                        </div>
                                    </div>
                                ))}
                            </Card>
                        ) : (
                            <CardError
                                icon={
                                    <ExportedImage
                                        src={process.env.NEXT_PUBLIC_BASE_PATH + "/svgs/HeadMaskIcon.svg"}
                                        alt="HeadMaskIcon"
                                        layout="fill"
                                        objectFit="contain"
                                    />
                                }
                            >
                                {chatbotLocalized?.maskFaceNotDetectedHint}
                            </CardError>
                        )}
                    </motion.div>
                    <div className="flex w-full flex-col gap-[8px]">
                        {selfieOrCall === "call" ?
                            <ButtonChat
                                onClick={() => {
                                    updateStartCountdown(true);
                                    requestToken();
                                    trackView("maskstartcountdown");
                                    screenView('1.4 - Face Recognition Countdown')
                                }}
                                iconRight={
                                    callButtonIcon ? (
                                        <ExportedImage
                                            src={getImageUrlFromGeneralImage(callButtonIcon)}
                                            alt={getAltTextFromGeneralImage(callButtonIcon)}
                                            layout="fill"
                                        />
                                    ) : undefined
                                }
                            >
                                {globalEventLocalized?.maskButtonCall.text}
                            </ButtonChat>
                            :
                            <ButtonChat
                                onClick={() => {
                                    updateStartCountdown(true);
                                    requestToken();
                                    trackView("maskstartcountdown");
                                    screenView('1.4 - Face Recognition Countdown')
                                }}
                                iconRight={
                                    selfieButtonIcon ? (
                                        <ExportedImage
                                            src={getImageUrlFromGeneralImage(selfieButtonIcon)}
                                            alt={getAltTextFromGeneralImage(selfieButtonIcon)}
                                            layout="fill"
                                        />
                                    ) : undefined
                                }
                            >
                                {globalEventLocalized?.maskButtonSelfie.text}
                            </ButtonChat>
                        }
                        <ButtonChat
                            onClick={onGoBack}
                            variant="secondary"
                            iconRight={
                                backButtonIcon ? (
                                    <ExportedImage
                                        src={getImageUrlFromGeneralImage(backButtonIcon)}
                                        alt={getAltTextFromGeneralImage(backButtonIcon)}
                                        layout="fill"
                                    />
                                ) : undefined
                            }
                        >
                            {maskLocalized?.buttonTwo.text}
                        </ButtonChat>
                    </div>
                </div>
            )}
        </div>
    );
};

export default inject("store")(observer(Mask));
